import { KeyboardEvent, PointerEvent, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { HelpCircle } from "lucide-react";

/**
 * Aiuto in pagina del modulo Ritiri valori: un pulsante che apre un breve
 * testo. Nell'app non c'era un componente tooltip da riusare.
 *
 * Si apre al passaggio del mouse, con la tastiera (fuoco da Tab, Invio o
 * Spazio) e con un tocco sul telefono; si chiude con Esc, togliendo il fuoco,
 * o con un clic/tocco fuori. Il testo è sempre nel DOM (nascosto quando
 * chiuso) e collegato al pulsante con `aria-describedby`, così un lettore di
 * schermo lo legge anche senza aprirlo.
 *
 * Aperto, il riquadro esce dal punto in cui sta il pulsante: va in un portale
 * con posizione fissa, così non lo tagliano i contenitori con overflow nascosto
 * (la testata) e non lo coprono le schede successive (ogni scheda animata ha un
 * proprio contesto di sovrapposizione). Il portale è la finestra modale che
 * contiene il pulsante, se c'è, altrimenti `body`: dentro una finestra il
 * riquadro resta nel dialogo (non inerte, letto come parte della finestra).
 */
interface Props {
  /** Il testo dell'aiuto. */
  testo: ReactNode;
  /** Nome accessibile del pulsante, es. "Aiuto: scadenza richieste". Ignorato con `children`. */
  etichetta?: string;
  /** Contenuto visibile del pulsante (es. il badge); senza, un'icona "?". */
  children?: ReactNode;
  /** Classi del pulsante. */
  className?: string;
  /** Su fondo scuro (la testata): icona chiara. */
  suScuro?: boolean;
}

/** Distanza dal pulsante e margine minimo dai bordi dello schermo, in px. */
const DISTANZA = 6;
const MARGINE = 8;
/** Aperto col mouse: attesa prima di chiudere, per passare dal pulsante al riquadro (o viceversa). */
const RITARDO_CHIUSURA = 150;

/** Il centro del pulsante è visibile: dentro lo schermo e dentro ogni contenitore che scorre o taglia. */
function inVista(el: HTMLElement) {
  if (el.getClientRects().length === 0) return false;
  const r = el.getBoundingClientRect();
  const x = r.left + r.width / 2;
  const y = r.top + r.height / 2;
  let [alto, sinistra, basso, destra] = [0, 0, document.documentElement.clientHeight, document.documentElement.clientWidth];
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const s = getComputedStyle(p);
    if (s.overflowX === "visible" && s.overflowY === "visible") continue;
    const c = p.getBoundingClientRect();
    [alto, sinistra, basso, destra] = [Math.max(alto, c.top), Math.max(sinistra, c.left), Math.min(basso, c.bottom), Math.min(destra, c.right)];
  }
  return x >= sinistra && x <= destra && y >= alto && y <= basso;
}

export function AiutoRitiri({ testo, etichetta, children, className, suScuro }: Props) {
  const id = useId();
  const contenitore = useRef<HTMLSpanElement>(null);
  const pulsante = useRef<HTMLButtonElement>(null);
  const riquadro = useRef<HTMLSpanElement>(null);
  const [fissato, setFissato] = useState(false);
  const [temporaneo, setTemporaneo] = useState(false);
  // Dove va il portale: la finestra modale che contiene il pulsante, o `body`.
  const [destinazione, setDestinazione] = useState<Element | null>(null);
  const timerChiusura = useRef<number>();
  const aperto = fissato || temporaneo;

  function preparaApertura() {
    setDestinazione(contenitore.current?.closest('[role="dialog"]') ?? document.body);
  }

  function chiudi() {
    annullaChiusura();
    setFissato(false);
    setTemporaneo(false);
  }

  // Col mouse l'aiuto resta aperto finché il puntatore è sul pulsante o sul
  // riquadro: uscendo da uno dei due si chiude dopo un attimo, a meno che il
  // puntatore non entri nell'altro (tra i due ci sono 6 px di vuoto).
  function annullaChiusura() {
    window.clearTimeout(timerChiusura.current);
  }
  function chiudiTraPoco() {
    annullaChiusura();
    timerChiusura.current = window.setTimeout(() => setTemporaneo(false), RITARDO_CHIUSURA);
  }
  useEffect(() => annullaChiusura, []);

  // Posizione: sotto il pulsante se c'è spazio, altrimenti sopra; allineato al
  // bordo del pulsante dalla parte dove c'è spazio e sempre dentro lo schermo.
  // Segue scorrimento e ridimensionamento; si chiude se il pulsante esce dalla vista.
  useLayoutEffect(() => {
    if (!aperto) return;
    const b = pulsante.current;
    const t = riquadro.current;
    if (!b || !t) return;
    const posiziona = () => {
      if (!inVista(b)) {
        chiudi();
        return;
      }
      const larghezza = document.documentElement.clientWidth;
      const altezza = document.documentElement.clientHeight;
      const p = b.getBoundingClientRect();
      const { width: w, height: h } = t.getBoundingClientRect();
      const sotto = p.bottom + DISTANZA;
      const sopra = p.top - DISTANZA - h;
      let alto: number;
      if (sotto + h <= altezza - MARGINE) alto = sotto;
      else if (sopra >= MARGINE) alto = sopra;
      else alto = altezza - p.bottom >= p.top ? sotto : sopra;
      alto = Math.max(MARGINE, Math.min(alto, altezza - MARGINE - h));
      let sinistra = p.left + p.width / 2 > larghezza / 2 ? p.right - w : p.left;
      sinistra = Math.max(MARGINE, Math.min(sinistra, larghezza - MARGINE - w));
      t.style.top = `${alto}px`;
      t.style.left = `${sinistra}px`;
    };
    posiziona();
    // In cattura: anche lo scorrimento dei contenitori interni (finestra, colonna del dettaglio).
    window.addEventListener("scroll", posiziona, true);
    window.addEventListener("resize", posiziona);
    return () => {
      window.removeEventListener("scroll", posiziona, true);
      window.removeEventListener("resize", posiziona);
    };
  }, [aperto, destinazione, testo]);

  // Clic o tocco fuori (dal pulsante e dal riquadro): chiude.
  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: globalThis.PointerEvent) => {
      const dove = e.target as Node;
      if (!contenitore.current?.contains(dove) && !riquadro.current?.contains(dove)) chiudi();
    };
    document.addEventListener("pointerdown", fuori);
    return () => document.removeEventListener("pointerdown", fuori);
  }, [aperto]);

  function tasto(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "Escape" && aperto) {
      // Chiude solo l'aiuto, non la finestra che lo contiene.
      e.stopPropagation();
      chiudi();
    }
  }

  const soloMouse = (f: () => void) => (e: PointerEvent) => {
    if (e.pointerType === "mouse") f();
  };

  const testoAiuto = (
    <span
      ref={riquadro}
      id={id}
      role="tooltip"
      hidden={!aperto}
      onPointerEnter={soloMouse(annullaChiusura)}
      onPointerLeave={soloMouse(chiudiTraPoco)}
      // Un clic sul testo non toglie il fuoco al pulsante (che chiuderebbe l'aiuto).
      onMouseDown={(e) => e.preventDefault()}
      className="fixed left-0 top-0 z-modal w-max max-w-[min(18rem,calc(100vw-2rem))] whitespace-normal rounded-xl border border-ritiri-bordo bg-white px-3 py-2 text-left text-xs font-medium normal-case leading-relaxed tracking-normal text-ink shadow-lift"
    >
      {testo}
    </span>
  );

  return (
    <span ref={contenitore} className="relative inline-flex align-middle">
      <button
        ref={pulsante}
        type="button"
        aria-describedby={id}
        aria-expanded={aperto}
        aria-label={children ? undefined : etichetta}
        onClick={() => {
          preparaApertura();
          setFissato((f) => !f);
        }}
        onPointerEnter={soloMouse(() => {
          annullaChiusura();
          preparaApertura();
          setTemporaneo(true);
        })}
        onPointerLeave={soloMouse(chiudiTraPoco)}
        onFocus={(e) => {
          // Solo il fuoco da tastiera apre: un tocco passa già dal clic.
          if (e.currentTarget.matches(":focus-visible")) {
            preparaApertura();
            setTemporaneo(true);
          }
        }}
        onBlur={chiudi}
        onKeyDown={tasto}
        className={clsx(
          "inline-flex items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          suScuro ? "focus-visible:outline-white" : "focus-visible:outline-primary",
          !children && (suScuro ? "text-ritiri-pillola hover:text-white" : "text-ritiri-th-testo hover:text-ink"),
          className
        )}
      >
        {children ?? <HelpCircle className="h-4 w-4" aria-hidden="true" />}
      </button>
      {/* Chiuso resta qui, nascosto (serve solo ad aria-describedby); aperto va nel portale. */}
      {aperto && destinazione ? createPortal(testoAiuto, destinazione) : testoAiuto}
    </span>
  );
}
